import React, { useCallback, useMemo, useReducer } from 'react';
import isNil from 'lodash/isNil';
// Components
import Modal from 'shared/modals/Modal';
import Button from 'shared/buttons';
import Preview from './Preview';
import rules from './rules';
// Context
import useRulesContext, { RulesContext } from './context';
// Styles
import './style.scss';
import { theme } from 'src/theme';

if (theme.mode === 'dark') {
  import(/* webpackChunkName: "dark" */ './style.scss?dark');
}

const reducer = (state, { type, payload }) => {
  switch (type) {
  case 'setRuleTypeId':
    return { ...state, ruleTypeId: payload };
  case 'setCount':
    return { ...state, count: payload };
  case 'setTerm':
    return { ...state, term: payload };
  case 'setValid':
    return { ...state, isValid: payload };
  case 'reset':
    return {
      ...state, count: null, term: '', isValid: false,
    };
  default:
    throw new Error(`Unhandled action type: ${type}`);
  }
};

const SubmitButton = ({ handleSubmit, handleClose }) => {
  const {
    ruleTypeId, count, term, isValid,
  } = useRulesContext();

  const onClick = () => {
    const transformation = {
      transformationType: ruleTypeId,
      stringParameter: term,
      numericParameter: count,
    };
    handleSubmit(transformation);
    handleClose();
  };

  return (
    <Button
      title='Set Rule'
      icon='check'
      themeColor='primary'
      onClick={onClick}
      disabled={!isValid}
    />
  );
};

const MappingRulesModal = ({ onClose, onSubmit, initialValue }) => {
  const initialState = {
    ruleTypeId: initialValue?.transformationType ?? null,
    count: initialValue?.numericParameter ?? null,
    term: initialValue?.stringParameter ?? '',
    isValid: !isNil(initialValue),
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const dispatchAction = useCallback((type, payload) => dispatch({ type, payload }), [dispatch]);
  const onTermChange = useCallback(({ value }) => dispatchAction('setTerm', value), [dispatchAction]);
  const onCountChange = useCallback(({ value }) => dispatchAction('setCount', value), [dispatchAction]);
  const onRuleTypeChange = useCallback((id) => dispatchAction('setRuleTypeId', id), [dispatchAction]);

  const contextValue = useMemo(() => ({
    ...state,
    onTermChange,
    onCountChange,
    onRuleTypeChange,
    dispatch: dispatchAction,
  }), [state, dispatchAction]);

  return (
    <Modal title='Set Mapping Rule' isOpen handleClose={onClose} containerStyle='small'>
      <RulesContext.Provider value={contextValue}>
        <div className='mapping-rules-wrapper'>
          <div className='mapping-rules'>
            <section className='mapping-preview'>
              <legend>Preview</legend>
              <div className='preview-visualization'><Preview /></div>
              <div className='preview-description'>
                <div className='description-item'>
                  <div className='preview-char' />
                  - excluded
                </div>
                <div className='description-item'>
                  <div className='preview-char included-char' />
                  - mapped character
                </div>
              </div>
            </section>
            <section className='mapping-rules-list-wrapper'>
              <legend>Mapping Rules</legend>
              <div className='mapping-rules-list'>
                {rules.map(({ RuleComponent, ruleTypesIds }) => <RuleComponent key={JSON.stringify(ruleTypesIds)} />)}
              </div>
            </section>
          </div>
          <section className='modal-actions-bar'>
            <Button
              title='Cancel'
              icon='x'
              themeColor='secondary'
              onClick={onClose}
            />
            <SubmitButton handleSubmit={onSubmit} handleClose={onClose} />
          </section>
        </div>
      </RulesContext.Provider>
    </Modal>
  );
};

export default MappingRulesModal;
